import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import { hasRole, useHasPerm } from '../lib/auth-store';
import {
  createAiCreditPack,
  getCoachingCredits,
  listAiCreditPacks,
  patchAiCreditPack,
  patchTenantFeatures,
  type CoachingCreditsSummary,
} from '../lib/api';

/**
 * Admin → Coaching → Credits (2026-10-13): the AI Credits a subscription
 * includes, what each action costs, the packs on sale, and what the month did.
 * Every number comes from the credit entries, the accounts, the orders and
 * the usage rows for the tenant's month; nothing on this page is a counter of
 * its own. The consumption table and the numbers it moves sit on one screen.
 */

const ACTION_LABELS: Record<string, string> = {
  kala_message: 'Master Kala message',
  blueprint: 'Success blueprint',
  daily_plan: 'Daily plan',
  guidance: 'Daily guidance',
  ritual_evening: 'Evening review',
  weekly_review: 'Weekly review',
  journal_reflection: 'Journal reflection',
  affirmation: 'Affirmation',
  action_suggestions: 'Action suggestions',
  alignment_todos: 'Alignment to-dos',
  aura: 'Aura reading',
  karma: 'Karma reading',
  palmistry: 'Palm reading',
  assistant: 'Store assistant',
};

const peso = (v: string | number) =>
  `₱${Number(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function CreditsTab() {
  const canWrite = useHasPerm('tenant.features.write') || hasRole('super_admin');
  const q = useQuery({ queryKey: ['coaching-credits'], queryFn: getCoachingCredits });
  if (q.isLoading || !q.data) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  const { month, position, economics, topConsumers } = q.data;
  const packVsCost =
    economics.costPerCreditPhp && economics.packPricePerCreditPhp
      ? Number(economics.packPricePerCreditPhp) >= Number(economics.costPerCreditPhp)
      : null;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>This month</CardTitle>
          <p className="text-sm text-muted-foreground">
            Counted in your own timezone, from the first of the month. A credit is spent only when
            the AI actually answered; the automatic daily texts spend them too.
          </p>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label="Included (minted by subscriptions)"
            value={month.minted.toLocaleString()}
            hint={`${economics.includedBundle} per paid month`}
          />
          <Stat
            label="Bought"
            value={month.purchased.toLocaleString()}
            hint={`${peso(month.purchasedPesos)} from packs and top-ups`}
          />
          <Stat
            label="Spent"
            value={month.spent.toLocaleString()}
            hint={`${month.refunded} refunded, ${month.expired} lapsed with the month`}
          />
          <Stat
            label="Earned and adjusted"
            value={month.earned.toLocaleString()}
            hint={`${month.adjustedNet >= 0 ? '+' : ''}${month.adjustedNet} by admin adjustments`}
          />
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Where members stand</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <Stat
              label="Subscribers"
              value={String(position.subscribers)}
              hint="with a live plan"
            />
            <Stat
              label="At zero"
              value={String(position.subscribersAtZero)}
              hint={`${position.subscribersBelowTen} more under 10 credits`}
            />
            <Stat
              label="Outstanding, included"
              value={position.outstandingIncluded.toLocaleString()}
              hint="lapse with each member's month"
            />
            <Stat
              label="Outstanding, bought"
              value={position.outstandingPurchased.toLocaleString()}
              hint={`never expire · ${position.toppedUpThisMonth} members bought this month`}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>What a credit costs</CardTitle>
            <p className="text-sm text-muted-foreground">
              Coaching AI spend this month divided by credits spent, at ₱{economics.usdPhpRate} to
              the dollar.
            </p>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <Stat
              label="Cost per credit"
              value={economics.costPerCreditPhp ? peso(economics.costPerCreditPhp) : '—'}
              hint={
                economics.costPerCreditUsd
                  ? `$${economics.costPerCreditUsd} · ${economics.creditsSpent} spent`
                  : 'nothing spent yet'
              }
            />
            <Stat
              label="Pack price per credit"
              value={economics.packPricePerCreditPhp ? peso(economics.packPricePerCreditPhp) : '—'}
              hint={
                packVsCost === null
                  ? 'cheapest pack on sale'
                  : packVsCost
                    ? 'covers the cost'
                    : 'BELOW the cost per credit'
              }
            />
            <Stat
              label="AI spend"
              value={`$${Number(economics.coachingSpendUsd).toFixed(2)}`}
              hint={peso(Number(economics.coachingSpendUsd) * economics.usdPhpRate)}
            />
            <Stat
              label="Included bundle"
              value={String(economics.includedBundle)}
              hint={
                economics.costPerCreditPhp
                  ? `≈ ${peso(economics.includedBundle * Number(economics.costPerCreditPhp))} if fully used`
                  : 'per paid month'
              }
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Spent by action</CardTitle>
          </CardHeader>
          <CardContent>
            {month.spentByAction.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing spent yet this month.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {month.spentByAction.map((row) => (
                    <tr key={row.action} className="border-b last:border-0">
                      <td className="py-1.5">{ACTION_LABELS[row.action] ?? row.action}</td>
                      <td className="py-1.5 text-right tabular-nums">{row.credits}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Top consumers</CardTitle>
          </CardHeader>
          <CardContent>
            {topConsumers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No credits spent yet this month.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="py-1 font-medium">Member</th>
                    <th className="py-1 text-right font-medium">Credits</th>
                    <th className="py-1 text-right font-medium">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {topConsumers.map((m) => (
                    <tr key={m.userId} className="border-b last:border-0">
                      <td className="py-1.5">
                        <Link to={`/members/${m.userId}`} className="underline underline-offset-2">
                          {m.name ?? m.email}
                        </Link>
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{m.credits}</td>
                      <td className="py-1.5 text-right tabular-nums">
                        {peso(Number(m.costUsd) * economics.usdPhpRate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      <CostsEditor costs={q.data.costs} canWrite={canWrite} />
      <PacksEditor canWrite={canWrite} />
    </div>
  );
}

/** The consumption table: credits per action, saved as `coaching.credit_costs`. */
function CostsEditor({
  costs,
  canWrite,
}: {
  costs: CoachingCreditsSummary['costs'];
  canWrite: boolean;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});
  useEffect(() => {
    setDraft(Object.fromEntries(costs.map((c) => [c.key, String(c.cost)])));
  }, [costs]);
  const save = useMutation({
    meta: { successMessage: 'Credit costs saved.' },
    mutationFn: () =>
      patchTenantFeatures({
        coaching: {
          credit_costs: Object.fromEntries(
            Object.entries(draft).map(([k, v]) => [k, Math.max(0, Math.floor(Number(v) || 0))]),
          ),
        },
      } as never),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['coaching-credits'] }),
  });
  const dirty = costs.some((c) => String(c.cost) !== (draft[c.key] ?? String(c.cost)));
  return (
    <Card>
      <CardHeader>
        <CardTitle>What each action costs</CardTitle>
        <p className="text-sm text-muted-foreground">
          In credits. 0 makes an action free. This changes what members are charged from the next
          action on; it never touches anyone&rsquo;s balance.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <table className="w-full text-sm">
          <tbody>
            {costs.map((c) => (
              <tr key={c.key} className="border-b last:border-0">
                <td className="py-2 pr-3">
                  <div>{c.label}</div>
                  <p className="text-xs text-muted-foreground">{c.what}</p>
                </td>
                <td className="py-2 text-right">
                  <Input
                    id={`credit-cost-${c.key}`}
                    className="ml-auto h-8 w-20 text-right"
                    inputMode="numeric"
                    disabled={!canWrite}
                    value={draft[c.key] ?? String(c.cost)}
                    onChange={(e) =>
                      setDraft({ ...draft, [c.key]: e.target.value.replace(/[^0-9]/g, '') })
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {canWrite && (
          <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            Save costs
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/** Credit packs: hidden virtual products members buy from the app when they run out. */
function PacksEditor({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const packs = useQuery({ queryKey: ['coaching-credit-packs'], queryFn: listAiCreditPacks });
  const [name, setName] = useState('');
  const [credits, setCredits] = useState('100');
  const [price, setPrice] = useState('50');
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['coaching-credit-packs'] });
    qc.invalidateQueries({ queryKey: ['coaching-credits'] });
  };
  const create = useMutation({
    meta: { successMessage: 'Pack created.' },
    mutationFn: () =>
      createAiCreditPack({
        name: name.trim(),
        credits: Math.floor(Number(credits)),
        price: price.trim(),
      }),
    onSuccess: () => {
      setName('');
      invalidate();
    },
  });
  const toggle = useMutation({
    mutationFn: (v: { productId: string; active: boolean }) =>
      patchAiCreditPack(v.productId, { active: v.active }),
    onSuccess: invalidate,
  });
  const rows = packs.data ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Credit packs</CardTitle>
        <p className="text-sm text-muted-foreground">
          What a member buys when they run out. A pack is a hidden virtual product: it is paid for
          like the plan (card or Manual Pay), never shows in the store, and its credits never
          expire. A withdrawn pack stops selling; credits already bought stay.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No packs yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-1 font-medium">Pack</th>
                <th className="py-1 text-right font-medium">Credits</th>
                <th className="py-1 text-right font-medium">Price</th>
                <th className="py-1 text-right font-medium">Sold</th>
                <th className="py-1 text-right font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.productId} className="border-b last:border-0">
                  <td className="py-1.5">
                    {p.name}{' '}
                    <Badge variant={p.active ? 'default' : 'secondary'} className="ml-1">
                      {p.active ? 'On sale' : 'Withdrawn'}
                    </Badge>
                    <p className="text-xs text-muted-foreground">{p.sku}</p>
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{p.credits}</td>
                  <td className="py-1.5 text-right tabular-nums">
                    {peso(p.price)}
                    <p className="text-xs text-muted-foreground">
                      {peso(Number(p.price) / Math.max(1, p.credits))} each
                    </p>
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{p.sold}</td>
                  <td className="py-1.5 text-right">
                    {canWrite && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={toggle.isPending}
                        onClick={() => toggle.mutate({ productId: p.productId, active: !p.active })}
                      >
                        {p.active ? 'Withdraw' : 'Put on sale'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {canWrite && (
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="pack-name">
                Name
              </label>
              <Input
                id="pack-name"
                className="h-8 w-56"
                placeholder="100 Kala credits"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="pack-credits">
                Credits
              </label>
              <Input
                id="pack-credits"
                className="h-8 w-24"
                inputMode="numeric"
                value={credits}
                onChange={(e) => setCredits(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="pack-price">
                Price (₱)
              </label>
              <Input
                id="pack-price"
                className="h-8 w-24"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ''))}
              />
            </div>
            <Button
              size="sm"
              disabled={create.isPending || !name.trim() || !Number(credits) || !Number(price)}
              onClick={() => create.mutate()}
            >
              Add pack
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
